import { useState, useEffect } from 'react';
import { supabase } from '../config/supabaseClient';
import {
    Organization, Location, Package, Category, Make, Machine, Service,
    OrganizationCategory, OrganizationMake, OrganizationData,
    PackageCategoryLink, PackageMakeLink, PackageStatusLink, PackageServiceLink,
    Discount, PackageDiscount, PackageLocationTypeLink,
    OrganizationSettings, OrganizationStatusSetting, OrganizationPhoneNumber,
    HaulingSettings,
    DisplayCategory, DisplayCategoryMapping
} from '../types';

interface UseOrganizationDataResult {
  data: OrganizationData | null;
  loading: boolean;
  error: string | null;
}

export function useOrganizationData(organizationId: string | undefined): UseOrganizationDataResult {
  const [data, setData] = useState<OrganizationData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!organizationId) {
      setError("Organization ID is missing.");
      setData(null);
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      setData(null);

      try {
        const orgIdNumber = parseInt(organizationId, 10);
        if (isNaN(orgIdNumber)) {
          throw new Error("Invalid Organization ID format.");
        }

        const [
          orgResponse,
          locationsResponse,
          packagesResponse,
          discountsResponse,
          categoriesResponse,
          makesResponse,
          machinesResponse,
          servicesResponse,
          orgCategoriesResponse,
          orgMakesResponse,
          settingsResponse,
          statusSettingsResponse,
          haulingSettingsResponse,
          haulingCategoryRatesResponse,
          haulingDiscountsResponse,
          phoneNumberResponse,
          displayCategoriesResponse,
          displayCategoryMappingsResponse
        ] = await Promise.all([
          supabase.from('organizations').select('id, name, email, tier').eq('id', orgIdNumber).maybeSingle(),
          supabase.from('locations').select('*').eq('organization_id', orgIdNumber),
          supabase.from('packages').select('*').eq('organization_id', orgIdNumber),
          supabase.from('discounts')
            .select('*')
            .eq('organization_id', orgIdNumber)
            .eq('active', true)
            .or(`expiration_date.gt.${new Date().toISOString()},expiration_date.is.null`),
          supabase.from('categories').select('*').order('name'),
          supabase.from('makes').select('*').order('name'),
          supabase.from('machines').select('*').order('name'),
          supabase.from('services').select('*').order('name'),
          supabase.from('organization_categories').select('organization_id, category_id').eq('organization_id', orgIdNumber),
          supabase.from('organization_makes').select('organization_id, make_id').eq('organization_id', orgIdNumber),
          supabase.from('organization_public_branding').select('primary_color, terms_link, logo_url').eq('organization_id', orgIdNumber).maybeSingle(),
          supabase.from('organization_status_settings').select('*').eq('organization_id', orgIdNumber),
          supabase.from('hauling_settings').select('*').eq('organization_id', orgIdNumber).maybeSingle(),
          supabase.from('hauling_category_rates').select('id, organization_id, category_id, per_mile_rate').eq('organization_id', orgIdNumber),
          supabase.from('hauling_discounts').select('*').eq('organization_id', orgIdNumber).eq('active', true),
          supabase.from('organization_phone_numbers').select('*').eq('organization_id', orgIdNumber).eq('is_primary', true).maybeSingle(),
          supabase.from('display_categories').select('id, organization_id, name').eq('organization_id', orgIdNumber).order('name'),
          supabase.from('display_category_mapping')
            .select('display_category_id, category_id, display_categories!inner(organization_id)')
            .eq('display_categories.organization_id', orgIdNumber)
        ]);

        const errors = [
            orgResponse.error, locationsResponse.error, packagesResponse.error, discountsResponse.error,
            categoriesResponse.error, makesResponse.error, machinesResponse.error,
            servicesResponse.error, orgCategoriesResponse.error, orgMakesResponse.error,
            settingsResponse.error, statusSettingsResponse.error, haulingSettingsResponse.error,
            haulingCategoryRatesResponse.error, haulingDiscountsResponse.error, phoneNumberResponse.error,
            displayCategoriesResponse.error, displayCategoryMappingsResponse.error
        ].filter(Boolean);

        if (errors.length > 0) {
           throw new Error(`Failed to fetch required data: ${errors[0]?.message || 'Unknown error'}`);
        }

        if (!orgResponse.data) {
          throw new Error(`Organization with ID ${organizationId} not found.`);
        }

        const orgPackageIds = (packagesResponse.data || []).map(p => p.id);

        let packageCatLinksData: PackageCategoryLink[] = [];
        let packageMakeLinksData: PackageMakeLink[] = [];
        let packageStatusLinksData: PackageStatusLink[] = [];
        let packageServiceLinksData: PackageServiceLink[] = [];
        let packageDiscountsData: PackageDiscount[] = [];
        let packageLocationLinksData: PackageLocationTypeLink[] = [];

        if (orgPackageIds.length > 0) {
            const [
                { data: catLinks, error: catLinkError },
                { data: makeLinks, error: makeLinkError },
                { data: statusLinks, error: statusLinkError },
                { data: serviceLinks, error: serviceLinkError },
                { data: discountLinks, error: discountLinkError },
                { data: locTypeLinks, error: locTypeError }
            ] = await Promise.all([
                supabase.from('package_categories').select('package_id, category_id').in('package_id', orgPackageIds),
                supabase.from('package_makes').select('package_id, make_id').in('package_id', orgPackageIds),
                supabase.from('package_statuses').select('package_id, status_value').in('package_id', orgPackageIds),
                supabase.from('package_services').select('package_id, service_id').in('package_id', orgPackageIds),
                supabase.from('packages_discounts').select('package_id, discount_id').in('package_id', orgPackageIds),
                supabase.from('package_location_types').select('package_id, location_preference').in('package_id', orgPackageIds)
            ]);

            const linkErrors = [catLinkError, makeLinkError, statusLinkError, serviceLinkError, discountLinkError, locTypeError].filter(Boolean);
            if (linkErrors.length > 0) {
              console.warn("Some package links failed to load:", linkErrors);
            }

            packageCatLinksData = catLinks || [];
            packageMakeLinksData = makeLinks || [];
            packageStatusLinksData = statusLinks || [];
            packageServiceLinksData = serviceLinks || [];
            packageDiscountsData = discountLinks || [];
            packageLocationLinksData = locTypeLinks || [];
        }

        // Transform hauling category rates to ensure per_mile_rate is a number
        const transformedHaulingCategoryRates = ((haulingCategoryRatesResponse.data || []) as any[]).map((rate: any) => ({
          ...rate,
          per_mile_rate: typeof rate.per_mile_rate === 'string' ? parseFloat(rate.per_mile_rate) : rate.per_mile_rate
        }));

        // Transform hauling settings to ensure numeric fields are numbers
        const transformedHaulingSettings = haulingSettingsResponse.data ? {
          ...haulingSettingsResponse.data,
          base_fee: typeof haulingSettingsResponse.data.base_fee === 'string' 
            ? parseFloat(haulingSettingsResponse.data.base_fee) 
            : haulingSettingsResponse.data.base_fee,
          minimum_distance_miles: typeof haulingSettingsResponse.data.minimum_distance_miles === 'string'
            ? parseFloat(haulingSettingsResponse.data.minimum_distance_miles)
            : haulingSettingsResponse.data.minimum_distance_miles
        } as HaulingSettings : null;

        // Transform hauling discounts to ensure numeric fields are numbers
        const transformedHaulingDiscounts = ((haulingDiscountsResponse.data || []) as any[]).map((discount: any) => ({
          ...discount,
          amount: typeof discount.amount === 'string' ? parseFloat(discount.amount) : discount.amount,
          min_distance: discount.min_distance && typeof discount.min_distance === 'string' 
            ? parseFloat(discount.min_distance) 
            : discount.min_distance
        }));

        const displayCategoryMappings: DisplayCategoryMapping[] = ((displayCategoryMappingsResponse.data || []) as any[]).map((row) => ({
          display_category_id: row.display_category_id,
          category_id: row.category_id,
        }));

        setData({
          organization: orgResponse.data as Organization,
          locations: (locationsResponse.data as Location[]) || [],
          packages: (packagesResponse.data as Package[]) || [],
          discounts: (discountsResponse.data as Discount[]) || [],
          allCategories: (categoriesResponse.data as Category[]) || [],
          allMakes: (makesResponse.data as Make[]) || [],
          allMachines: (machinesResponse.data as Machine[]) || [],
          allServices: (servicesResponse.data as Service[]) || [],
          organizationCategories: (orgCategoriesResponse.data as OrganizationCategory[]) || [],
          organizationMakes: (orgMakesResponse.data as OrganizationMake[]) || [],
          displayCategories: (displayCategoriesResponse.data as DisplayCategory[]) || [],
          displayCategoryMappings,
          packageCategoryLinks: packageCatLinksData,
          packageMakeLinks: packageMakeLinksData,
          packageStatusLinks: packageStatusLinksData,
          packageServiceLinks: packageServiceLinksData,
          packageDiscounts: packageDiscountsData,
          packageLocationTypeLinks: packageLocationLinksData,
          organizationSettings: (settingsResponse.data as OrganizationSettings | null),
          organizationStatusSettings: (statusSettingsResponse.data as OrganizationStatusSetting[]) || [],
          organizationPhoneNumber: (phoneNumberResponse.data as OrganizationPhoneNumber | null),
          haulingSettings: transformedHaulingSettings,
          haulingCategoryRates: transformedHaulingCategoryRates,
          haulingDiscounts: transformedHaulingDiscounts
        });

      } catch (err: any) {
        console.error("Error in useOrganizationData hook:", err);
        setError(err.message || "An unexpected error occurred while fetching form configuration.");
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [organizationId]);

  return { data, loading, error };
}